import { useNavigate } from 'react-router-dom';
import { LogOut, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { getBusinessDetails } from '@/utils/businessAppApi';
import axios from 'axios';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

export function BusinessHeader() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { t } = useTranslation('business', { keyPrefix: 'main' });
  const { t: tNav } = useTranslation('navigation');
  
  // Business-specific logout that clears both auth stores
  const handleBusinessLogout = () => {
    localStorage.removeItem('businessAuth');
    logout(); // This will clear regular auth
  };
  const [businessName, setBusinessName] = useState<string>(user?.businessName || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch business name from API if not available in user object
  useEffect(() => {
    const fetchBusinessProfile = async () => {
      // If we already have a business name from the user object, use it
      if (user?.businessName) {
        console.log('Using business name from user object:', user.businessName);
        setBusinessName(user.businessName);
        return;
      }
      
      setIsLoading(true);
      
      try {
        console.log('Fetching business profile from API...');
        // First attempt: use getBusinessDetails from businessAppApi
        try {
          const result = await getBusinessDetails();
          console.log('Business profile API response:', result);
          
          // Check all possible data structures
          if (result?.data?.data?.name) {
            console.log('Found name in result.data.data:', result.data.data.name);
            setBusinessName(result.data.data.name);
            return;
          } else if (result?.data?.name) {
            console.log('Found name in result.data:', result.data.name);
            setBusinessName(result.data.name);
            return;
          } else if (result?.data?.businessName) {
            console.log('Found businessName in result.data:', result.data.businessName);
            setBusinessName(result.data.businessName);
            return;
          } else if (result?.data?.data?.businessName) {
            console.log('Found businessName in result.data.data:', result.data.data.businessName);
            setBusinessName(result.data.data.businessName);
            return;
          } else if (result?.businessName) {
            console.log('Found businessName directly in result:', result.businessName);
            setBusinessName(result.businessName);
            return;
          } else if (result?.name) {
            console.log('Found name directly in result:', result.name);
            setBusinessName(result.name);
            return;
          } else {
            console.log('Business name not found in initial API response', result);
          }
        } catch (err) {
          console.error('Error using getBusinessDetails:', err);
        }
        
        // Second attempt: direct API call as fallback
        try {
          console.log('Attempting direct API call as fallback');
          const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
          
          // Get auth token from localStorage (business auth first, then fallback)
          const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
          const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
          const auth = businessAuth.token ? businessAuth : publicAuth;
          const token = auth.token;
          
          if (!token) {
            console.error('No auth token available');
            throw new Error('Authentication required');
          }
          
          const response = await axios.get(`${API_URL}/business/profile`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          console.log('Direct API response:', response.data);
          
          if (response.data?.data?.name) {
            console.log('Found business name in direct API call:', response.data.data.name);
            setBusinessName(response.data.data.name);
            return;
          } else if (response.data?.data?.businessName) {
            console.log('Found businessName in direct API call:', response.data.data.businessName);
            setBusinessName(response.data.data.businessName);
            return;
          } else if (response.data?.businessName) {
            console.log('Found business name in direct API call:', response.data.businessName);
            setBusinessName(response.data.businessName);
            return;
          } else if (response.data?.name) {
            console.log('Found name in direct API call:', response.data.name);
            setBusinessName(response.data.name);
            return;
          }
        } catch (apiError) {
          console.error('Error in direct API call:', apiError);
        }
        
        // If we reach here, we couldn't get the business name
        console.warn('Could not retrieve business name, using fallback');
        setBusinessName(t('yourBusiness'));
      } catch (error) {
        console.error('Error fetching business profile:', error);
        setBusinessName(t('yourBusiness'));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBusinessProfile();
  }, [user]);

  return (
    <TooltipProvider>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 100 }}
        className="fixed top-0 left-0 right-0 h-16 bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-600 text-white px-4 sm:px-6 flex sm:grid sm:grid-cols-3 items-center z-50 shadow-xl shadow-emerald-900/25 border-b border-emerald-400/30"
      >
        {/* Left: Logo/Brand — desktop only */}
        <div className="hidden sm:block col-span-1">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/business')}
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-xl font-bold text-white/90 whitespace-nowrap"
            >
              {t('businessSoftware')}
            </motion.span>
          </motion.div>
        </div>

        {/* Business Name — centered on desktop, left-aligned with flex-1 on mobile */}
        <div className="flex-1 sm:flex-none sm:col-span-1 flex sm:justify-center items-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`px-3 sm:px-6 py-1.5 sm:py-2 rounded-xl ${isLoading ? 'bg-white/10 border border-white/20' : 'bg-white/15 border border-white/30 shadow-lg shadow-emerald-900/20'} backdrop-blur-sm`}
          >
            {isLoading ? (
              <div className="h-5 sm:h-7 w-24 sm:w-32 animate-pulse bg-white/20 rounded-full" />
            ) : (
              <span className="text-sm sm:text-xl font-semibold text-white tracking-wide whitespace-nowrap">
                {businessName || t('yourBusiness')}
              </span>
            )}
          </motion.div>
        </div>

        {/* Right: Actions */}
        <div className="sm:col-span-1 flex justify-end items-center gap-2 sm:gap-4">
          {/* Language Switcher — desktop only */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="hidden sm:block">
            <LanguageSwitcher
              variant="outline"
              className="border-white/40 text-gray-600 hover:bg-green-100 hover:text-green-600 hover:border-white/60 rounded-xl transition-all duration-300 shadow-md backdrop-blur-sm font-medium"
              dropdownClassName="backdrop-blur-sm bg-white/95"
              showText={false}
            />
          </motion.div>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="outline"
                  className="border-white/40 text-gray-600 hover:bg-red-100 hover:text-red-600 hover:border-white/60 rounded-xl transition-all duration-300 shadow-md backdrop-blur-sm font-medium"
                  onClick={handleBusinessLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {tNav('logout')}
                </Button>
              </motion.div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-white/95 backdrop-blur-sm">
              <p className="text-gray-800">{tNav('logoutTooltip')}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </motion.header>
    </TooltipProvider>
  );
} 